import { z } from 'zod';

// --- 1. TYPE DEFINITIONS (The Autocomplete Magic) ---

export type MetaStandardEvent =
  | 'PageView'
  | 'Contact'
  | 'CompleteRegistration'
  | 'Lead'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'Purchase'
  | 'ViewContent'
  | 'Schedule';

export type MetaSalonEvent =
  | 'WhatsAppClick'
  | 'PortfolioView'
  | 'StylistSelected'
  | 'ServiceCategoryView'
  | 'LocationView'
  | 'PriceListDownload';

/**
 * The (string & {}) trick prevents the union from collapsing into a generic string.
 * This allows your IDE to suggest the literals above while still accepting any string.
 */
export type MetaEventName = MetaStandardEvent | MetaSalonEvent | (string & {});

// --- 2. ZOD SCHEMAS ---

// Standard Meta Events
export const MetaStandardEventSchema = z.enum([
  'PageView',
  'Contact',
  'CompleteRegistration',
  'Lead',
  'AddToCart',
  'InitiateCheckout',
  'Purchase',
  'ViewContent',
  'Schedule',
]);

// Custom Salon-Specific Events + Generic String fallback
export const MetaCustomEventSchema = z
  .string()
  .trim()
  .min(1, 'Event name cannot be empty');

// Identity (PII)
export const MetaUserBaseSchema = z.object({
  phone: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  external_id: z.string().nullable().optional(),
});

export const MetaOnlineUserSchema = MetaUserBaseSchema.extend({
  fbp: z.string().nullable().optional(),
  fbc: z.string().nullable().optional(),
  client_ip_address: z.string().optional(),
  client_user_agent: z.string().optional(),
  // Los arreglos hasheados para CAPI
  ph: z.array(z.string()).optional(),
  fn: z.array(z.string()).optional(),
  em: z.array(z.string()).optional(),
  // 🚩 Ciudad/Estado para mejorar el match en Venezuela
  ct: z.array(z.string()).optional(), // City
  st: z.array(z.string()).optional(), // State
});

export const MetaEventValueSchema = z.object({
  value: z.number().min(0, 'Value cannot be negative').optional(),
  currency: z.literal('USD').default('USD').optional(),
  content_name: z.string().optional(),
  content_category: z.string().optional(),

  // 🎯 Remarketing Dinámico e Inventario
  content_ids: z.array(z.string()).optional(),
  content_type: z.string().default('product').optional(),
  num_items: z.number().optional(),

  // 💎 Estructura detallada (Standard Meta)
  contents: z
    .array(
      z.object({
        id: z.string(),
        quantity: z.number().default(1),
        item_price: z.number().optional(),
        title: z.string().optional(),
        category: z.string().optional(),
      }),
    )
    .optional(),

  // 📅 BOOKING INTELLIGENCE
  appointment_id: z.string().optional(),
  booking_date: z.string().optional(),
  booking_time: z.string().optional(),
  booking_worker: z.string().optional(),
  order_id: z.string().optional(),
  // 💳 FINANCIAL INTELLIGENCE (Los campos que te faltan)
  payment_info_type: z.string().optional(), // 'transfer', 'cash', 'zelle', etc.
  payment_method_id: z.string().optional(), // Tu ID interno de Firestore
  payment_method_title: z.string().optional(), // El nombre legible (ej: "Pago Móvil Mercantil")

  // 💎 Overrides
  eventName: z.string().optional(), // Para mapear de TufirmaEvent a MetaStandardEvent
  
  // 🔍 Otros campos de búsqueda y contexto
  search_string: z.string().optional(),
  description: z.string().optional(),
});
// Unified Payload for API Routes
export const MetaPayloadSchema = z.object({
  salonId: z.string().min(1, 'salonId is required'),
  // We cast the union to our custom type to enable IDE suggestions
  eventName: z.union([
    MetaStandardEventSchema,
    MetaCustomEventSchema,
  ]) as z.ZodType<MetaEventName>,
  userData: MetaOnlineUserSchema,
  customData: MetaEventValueSchema.optional(),
  eventId: z.string().min(1, 'eventId is required for deduplication'),
  eventSourceUrl: z.string().url().optional(),
  testEventCode: z.string().optional(),
});

// Firestore Config
export const SalonMarketingConfigSchema = z.object({
  pixelId: z.string().min(1),
  accessToken: z.string().min(1),
  isMasterEnabled: z.boolean().default(true),
  updatedAt: z.any().optional(),
});

// --- 3. EXPORTED TYPES ---

export type MetaOnlineUser = z.infer<typeof MetaOnlineUserSchema>;
export type MetaEventValue = z.infer<typeof MetaEventValueSchema>;
export type MetaPayload = z.infer<typeof MetaPayloadSchema>;
export type SalonMarketingConfig = z.infer<typeof SalonMarketingConfigSchema>;
